import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useAuth } from "AuthContext";
import {
  Tabs, Tab, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Tooltip, IconButton, Chip,
} from "@mui/material";

const apiCall = (url, method, body, token) =>
  fetch(url, {
    method: method || "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then((r) => r.json());

function SystemSettings() {
  const { token } = useAuth();
  const [tabValue, setTabValue] = useState(0);

  // --- State ---
  const [tiers, setTiers] = useState([]);
  const [options, setOptions] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [rechargePlans, setRechargePlans] = useState([]);
  const [menuSettings, setMenuSettings] = useState([]);

  // --- Dialog state ---
  const [dialog, setDialog] = useState({ open: false, mode: "add", section: "", data: {} });
  const closeDialog = () => setDialog({ open: false, mode: "add", section: "", data: {} });
  const openAdd = (section) => setDialog({ open: true, mode: "add", section, data: {} });
  const openEdit = (section, data) => setDialog({ open: true, mode: "edit", section, data: { ...data } });

  // --- Fetch helpers ---
  const fetchTiers = () => apiCall("/api/admin/creator-tiers", "GET", null, token).then(d => setTiers(Array.isArray(d) ? d : [])).catch(() => setTiers([]));
  const fetchOptions = () => apiCall("/api/admin/options", "GET", null, token).then(d => setOptions(Array.isArray(d) ? d : [])).catch(() => setOptions([]));
  const fetchConfigs = () => apiCall("/api/admin/configs", "GET", null, token).then(d => setConfigs(Array.isArray(d) ? d : [])).catch(() => setConfigs([]));
  const fetchPlans = () => apiCall("/api/admin/recharge-plans", "GET", null, token).then(d => setRechargePlans(Array.isArray(d) ? d : [])).catch(() => setRechargePlans([]));
  const fetchMenuSettings = () => apiCall("/api/admin/menu-settings", "GET", null, token).then(d => setMenuSettings(Array.isArray(d) ? d : [])).catch(() => setMenuSettings([]));

  useEffect(() => { fetchTiers(); fetchOptions(); fetchConfigs(); fetchPlans(); fetchMenuSettings(); }, [token]);

  // --- Save handler ---
  const handleSave = async () => {
    const { mode, section, data } = dialog;
    const isEdit = mode === "edit";
    let url = "";

    try {
      if (section === "option") {
        url = isEdit ? "/api/admin/options/update" : "/api/admin/options/create";
        await apiCall(url, "POST", data, token);
        fetchOptions();
      } else if (section === "tier") {
        url = isEdit ? "/api/admin/creator-tiers/update" : "/api/admin/creator-tiers/create";
        await apiCall(url, "POST", data, token);
        fetchTiers();
      } else if (section === "plan") {
        url = isEdit ? "/api/admin/recharge-plans/update" : "/api/admin/recharge-plans/create";
        await apiCall(url, "POST", data, token);
        fetchPlans();
      } else if (section === "config") {
        await apiCall("/api/admin/configs/update", "POST", data, token);
        fetchConfigs();
      } else if (section === "menu") {
        await apiCall("/api/admin/menu-settings/update", "POST", data, token);
        fetchMenuSettings();
      }
      closeDialog();
    } catch (e) {
      console.error("Save failed:", e);
    }
  };

  // --- Delete handler ---
  const handleDelete = async (section, id) => {
    if (!window.confirm("確認刪除？此操作無法復原。")) return;
    const urlMap = {
      option: "/api/admin/options/delete",
      tier: "/api/admin/creator-tiers/delete",
      plan: "/api/admin/recharge-plans/delete",
    };
    await apiCall(urlMap[section], "POST", { id }, token);
    if (section === "option") fetchOptions();
    if (section === "tier") fetchTiers();
    if (section === "plan") fetchPlans();
  };

  // --- Field change helper ---
  const setField = (field, value) =>
    setDialog((prev) => ({ ...prev, data: { ...prev.data, [field]: value } }));

  // --- Dialog Content ---
  const renderDialogContent = () => {
    const { section, data } = dialog;

    if (section === "option") {
      return (
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField select label="類型" value={data.type || ""} onChange={e => setField("type", e.target.value)} size="small" SelectProps={{ native: true }}>
            <option value="">--選擇--</option>
            <option value="SYMBOL">標的 (SYMBOL)</option>
            <option value="TIMEFRAME">週期 (TIMEFRAME)</option>
            <option value="DIRECTION">方向 (DIRECTION)</option>
          </TextField>
          <TextField label="值 (Value)" value={data.value || ""} onChange={e => setField("value", e.target.value)} size="small" />
          <TextField label="顯示標籤 (Label)" value={data.label || ""} onChange={e => setField("label", e.target.value)} size="small" />
          <TextField label="排序 (Order)" type="number" value={data.order ?? ""} onChange={e => setField("order", parseInt(e.target.value))} size="small" />
          <FormControlLabel
            control={<Switch checked={!!data.isVisible} onChange={e => setField("isVisible", e.target.checked)} />}
            label="顯示"
          />
        </MDBox>
      );
    }

    if (section === "tier") {
      return (
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="等級名稱" value={data.name || ""} onChange={e => setField("name", e.target.value)} size="small" />
          <TextField label="抽成比例 (%)" type="number" value={data.commissionRate ?? ""} onChange={e => setField("commissionRate", e.target.value)} size="small" />
          <TextField label="最低總回報 (%)" type="number" value={data.minTotalReturn ?? ""} onChange={e => setField("minTotalReturn", e.target.value)} size="small" />
          <TextField label="最大回撤上限 (%)" type="number" value={data.maxMDD ?? ""} onChange={e => setField("maxMDD", e.target.value)} size="small" />
          <TextField label="最低勝率 (%)" type="number" value={data.minWinRate ?? ""} onChange={e => setField("minWinRate", e.target.value)} size="small" />
          <TextField label="最低 Profit Factor" type="number" value={data.minProfitFactor ?? ""} onChange={e => setField("minProfitFactor", e.target.value)} size="small" />
          <TextField label="最低交易次數" type="number" value={data.minTrades ?? ""} onChange={e => setField("minTrades", e.target.value)} size="small" />
        </MDBox>
      );
    }

    if (section === "plan") {
      return (
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="方案名稱" value={data.name || ""} onChange={e => setField("name", e.target.value)} size="small" />
          <TextField label="金額 (USD)" type="number" value={data.usdAmount ?? ""} onChange={e => setField("usdAmount", e.target.value)} size="small" />
          <TextField label="MAS Coin 數量" type="number" value={data.coinAmount ?? ""} onChange={e => setField("coinAmount", e.target.value)} size="small" />
          {dialog.mode === "edit" && (
            <FormControlLabel
              control={<Switch checked={!!data.isActive} onChange={e => setField("isActive", e.target.checked)} />}
              label="啟用"
            />
          )}
        </MDBox>
      );
    }

    if (section === "config") {
      return (
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="鍵名 (Key)" value={data.key || ""} disabled size="small" />
          <TextField label="值 (Value)" value={data.value ?? ""} onChange={e => setField("value", e.target.value)} size="small" />
          <TextField label="說明 (Description)" multiline rows={2} value={data.description ?? ""} onChange={e => setField("description", e.target.value)} size="small" />
        </MDBox>
      );
    }

    if (section === "menu") {
      return (
        <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="選單 Key" value={data.menuKey || ""} disabled size="small" />
          <TextField label="顯示名稱" value={data.name || ""} onChange={e => setField("name", e.target.value)} size="small" />
          <FormControlLabel
            control={<Switch checked={!!data.isVisible} onChange={e => setField("isVisible", e.target.checked)} />}
            label="顯示此選單"
          />
        </MDBox>
      );
    }

    return null;
  };

  const titleMap = { add: "新增", edit: "編輯" };
  const sectionNameMap = { option: "選單選項", tier: "KPI 等級", plan: "儲值方案", config: "系統設定", menu: "選單顯示設定" };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={2}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="動態選單 (標的/週期/方向)" />
            <Tab label="KPI 門檻 (創作者等級)" />
            <Tab label="系統全域變數" />
            <Tab label="儲值方案 (MAS Coin)" />
            <Tab label="選單顯示管理" />
          </Tabs>
        </MDBox>

        {/* ─────────────── Tab 0: 動態選單 ─────────────── */}
        {tabValue === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <MDBox display="flex" justifyContent="flex-end" mb={1}>
                <MDButton variant="contained" color="info" size="small" onClick={() => openAdd("option")}>
                  <Icon sx={{ mr: 0.5 }}>add</Icon> 新增選項
                </MDButton>
              </MDBox>
            </Grid>
            {["SYMBOL", "TIMEFRAME", "DIRECTION"].map((type) => (
              <Grid item xs={12} key={type}>
                <Card>
                  <MDBox p={3}>
                    <MDTypography variant="h6" mb={2}>
                      {type === "SYMBOL" ? "標的 (Symbols)" : type === "TIMEFRAME" ? "週期 (Timeframes)" : "方向 (Directions)"}
                    </MDTypography>
                    <DataTable
                      table={{
                        columns: [
                          { Header: "值", accessor: "value", width: "15%" },
                          { Header: "顯示標籤", accessor: "label", width: "20%" },
                          { Header: "排序", accessor: "order", width: "10%" },
                          {
                            Header: "顯示",
                            accessor: "isVisible",
                            Cell: ({ row }) => (
                              <Switch
                                checked={!!row.original.isVisible}
                                onChange={async (e) => {
                                  await apiCall("/api/admin/options/update", "POST", { id: row.original.id, isVisible: e.target.checked }, token);
                                  fetchOptions();
                                }}
                              />
                            ),
                          },
                          {
                            Header: "操作",
                            accessor: "id",
                            Cell: ({ row }) => (
                              <MDBox display="flex" gap={1}>
                                <Tooltip title="編輯">
                                  <IconButton size="small" onClick={() => openEdit("option", row.original)}>
                                    <Icon fontSize="small" color="info">edit</Icon>
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="刪除">
                                  <IconButton size="small" onClick={() => handleDelete("option", row.original.id)}>
                                    <Icon fontSize="small" color="error">delete</Icon>
                                  </IconButton>
                                </Tooltip>
                              </MDBox>
                            ),
                          },
                        ],
                        rows: options.filter((o) => o.type === type),
                      }}
                      isSorted={false}
                      entriesPerPage={false}
                      noEndBorder
                    />
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* ─────────────── Tab 1: KPI 門檻 ─────────────── */}
        {tabValue === 1 && (
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">創作者 KPI 門檻設定</MDTypography>
                <MDButton variant="contained" color="info" size="small" onClick={() => openAdd("tier")}>
                  <Icon sx={{ mr: 0.5 }}>add</Icon> 新增等級
                </MDButton>
              </MDBox>
              <DataTable
                table={{
                  columns: [
                    { Header: "等級名稱", accessor: "name", width: "15%" },
                    { Header: "抽成 (%)", accessor: "commissionRate", width: "12%" },
                    { Header: "最低總回報 (%)", accessor: "minTotalReturn", width: "15%" },
                    { Header: "最大回撤 (%)", accessor: "maxMDD", width: "12%" },
                    { Header: "最低勝率 (%)", accessor: "minWinRate", width: "12%" },
                    { Header: "Profit Factor", accessor: "minProfitFactor", width: "12%" },
                    { Header: "最低次數", accessor: "minTrades", width: "10%" },
                    {
                      Header: "操作",
                      accessor: "id",
                      Cell: ({ row }) => (
                        <MDBox display="flex" gap={1}>
                          <Tooltip title="編輯">
                            <IconButton size="small" onClick={() => openEdit("tier", row.original)}>
                              <Icon fontSize="small" color="info">edit</Icon>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="刪除">
                            <IconButton size="small" onClick={() => handleDelete("tier", row.original.id)}>
                              <Icon fontSize="small" color="error">delete</Icon>
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                      ),
                    },
                  ],
                  rows: tiers,
                }}
                isSorted={false}
                entriesPerPage={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        )}

        {/* ─────────────── Tab 2: 系統全域變數 ─────────────── */}
        {tabValue === 2 && (
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={3}>系統全域變數</MDTypography>
              <Grid container spacing={3}>
                {configs.map((config) => (
                  <Grid item xs={12} md={6} key={config.key}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                        <MDBox flexGrow={1} mr={2}>
                          <MDTypography variant="subtitle2" fontWeight="bold">{config.key}</MDTypography>
                          <MDTypography variant="caption" color="text">{config.description}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={1}>
                          <Chip label={config.value} size="small" color="info" variant="outlined" />
                          <Tooltip title="編輯">
                            <IconButton size="small" onClick={() => openEdit("config", config)}>
                              <Icon fontSize="small" color="info">edit</Icon>
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </MDBox>
          </Card>
        )}

        {/* ─────────────── Tab 3: 儲值方案 ─────────────── */}
        {tabValue === 3 && (
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">MAS Coin 儲值方案設定</MDTypography>
                <MDButton variant="contained" color="info" size="small" onClick={() => openAdd("plan")}>
                  <Icon sx={{ mr: 0.5 }}>add</Icon> 新增方案
                </MDButton>
              </MDBox>
              <DataTable
                table={{
                  columns: [
                    { Header: "名稱", accessor: "name", width: "20%" },
                    { Header: "金額 (USD)", accessor: "usdAmount", width: "15%" },
                    { Header: "MAS Coin 數量", accessor: "coinAmount", width: "15%" },
                    {
                      Header: "狀態",
                      accessor: "isActive",
                      Cell: ({ row }) => (
                        <Switch
                          checked={!!row.original.isActive}
                          onChange={async (e) => {
                            await apiCall("/api/admin/recharge-plans/update", "POST", { id: row.original.id, ...row.original, isActive: e.target.checked }, token);
                            fetchPlans();
                          }}
                        />
                      ),
                    },
                    {
                      Header: "操作",
                      accessor: "id",
                      Cell: ({ row }) => (
                        <MDBox display="flex" gap={1}>
                          <Tooltip title="編輯">
                            <IconButton size="small" onClick={() => openEdit("plan", row.original)}>
                              <Icon fontSize="small" color="info">edit</Icon>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="刪除">
                            <IconButton size="small" onClick={() => handleDelete("plan", row.original.id)}>
                              <Icon fontSize="small" color="error">delete</Icon>
                            </IconButton>
                          </Tooltip>
                        </MDBox>
                      ),
                    },
                  ],
                  rows: rechargePlans,
                }}
                isSorted={false}
                entriesPerPage={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        )}

        {/* ─────────────── Tab 4: 選單顯示管理 ─────────────── */}
        {tabValue === 4 && (
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" mb={2}>側邊欄選單顯示管理</MDTypography>
              <DataTable
                table={{
                  columns: [
                    { Header: "選單 Key", accessor: "menuKey", width: "25%" },
                    { Header: "顯示名稱 (管理用)", accessor: "name", width: "30%" },
                    {
                      Header: "顯示狀態",
                      accessor: "isVisible",
                      Cell: ({ row }) => (
                        <MDBox display="flex" alignItems="center">
                          <Switch
                            checked={!!row.original.isVisible}
                            onChange={async (e) => {
                              await apiCall("/api/admin/menu-settings/update", "POST", { menuKey: row.original.menuKey, isVisible: e.target.checked }, token);
                              fetchMenuSettings();
                            }}
                          />
                          <MDTypography variant="caption" color={row.original.isVisible ? "success" : "error"} fontWeight="medium">
                            {row.original.isVisible ? "顯示中" : "已隱藏"}
                          </MDTypography>
                        </MDBox>
                      ),
                    },
                    {
                      Header: "操作",
                      accessor: "id",
                      Cell: ({ row }) => (
                        <Tooltip title="編輯名稱">
                          <IconButton size="small" onClick={() => openEdit("menu", row.original)}>
                            <Icon fontSize="small" color="info">edit</Icon>
                          </IconButton>
                        </Tooltip>
                      ),
                    },
                  ],
                  rows: menuSettings,
                }}
                isSorted={false}
                entriesPerPage={false}
                noEndBorder
              />
            </MDBox>
          </Card>
        )}
      </MDBox>

      {/* ─────────────── Universal CRUD Dialog ─────────────── */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {titleMap[dialog.mode] || ""} {sectionNameMap[dialog.section] || ""}
        </DialogTitle>
        <DialogContent>{renderDialogContent()}</DialogContent>
        <DialogActions>
          <MDButton onClick={closeDialog} color="secondary">取消</MDButton>
          <MDButton onClick={handleSave} color="info" variant="contained">
            {dialog.mode === "edit" ? "儲存修改" : "確認新增"}
          </MDButton>
        </DialogActions>
      </Dialog>

    </DashboardLayout>
  );
}

export default SystemSettings;
